import { useEffect, useState } from 'react';
import { HighlightOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Controller, useForm } from 'react-hook-form';
import CustomizedSnackbars from '../../Notifications/SnackBar';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useLocation, withRouter } from 'react-router-dom/cjs/react-router-dom';
import ButtonBack from '../../Breadcrumb/ButtonBack';
import { LoadingButton } from '@mui/lab';
import PrescripcionElectronicaTableResult from './PrescripcionElectronicaTableResult';
import { useBuscarPrescripcionElectronica } from '../hooks/useBuscarPrescripcionElectronica';
import AfiliadoSelect from '../../Afiliados/AfiliadoSelect';

const BuscarPrescripcionesElectronicas = (props) => {
  const { id } = useParams();
  const { state } = useLocation();
  const [selectedAfiliado, setSelectedAfiliado] = useState(null);
  const [
    keyComponentResultPrescripcionesElectronicas,
    setKeyComponentResultPrescripcionesElectronicas,
  ] = useState(0);

  const {
    dataForm,
    prescripcionesElectronicas,
    messageStatus,
    isLoading,
    showForm,
    setAndShowMessage,
    onHandlerBackToForm,
    onHandlerCleanMessage,
    handlerFindPrescripcionesElectronicasSubmit,
  } = useBuscarPrescripcionElectronica({
    idAfiliado: id || null,
    locationState: state?.datosBack || null,
  });

  const {
    register,
    control,
    getValues,
    setValue,
    trigger,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    values: dataForm,
  });

  const refresh = () => {
    setKeyComponentResultPrescripcionesElectronicas(1 + Math.random() * (1000 - 1));
    onHandleSubmitForm(dataForm);
  };

  /**
   * Función submit de la consulta
   * @param {object} data
   * @returns
   */
  const onHandleSubmitForm = (data) => {
    if ((!data.idAfiliado, !data.documento && !data?.fechaDesde && !data?.fechaHasta)) {
      setAndShowMessage({
        status: 'error',
        message: 'Debes ingresar por lo menos un criterio para realizar la búsqueda',
      });
      return;
    }

    handlerFindPrescripcionesElectronicasSubmit(data);
  };

  /**
   * Función de limpieza formulario
   */
  const onHandlerLimpiar = () => {
    reset();
    setKeyComponentResultPrescripcionesElectronicas(1 + Math.random() * (1000 - 1));
  };

  return (
    <Grid container spacing={2}>
      {id && <ButtonBack />}
      <Grid item xs={12}>
        <Card sx={{ width: '100%', p: 1 }}>
          <CardContent>
            <Typography gutterBottom variant='h5' component='h2'>
              Buscar Prescripción Electrónica
            </Typography>
            <Typography
              variant='body2'
              color='textSecondary'
              component='p'
              align='justify'
              gutterBottom
            >
              Acá podrás buscar las prescripciones electrónicas según afiliado y/o en un rango de
              fecha.
            </Typography>
          </CardContent>

          {showForm && (
            <form onSubmit={handleSubmit((data) => onHandleSubmitForm(data))}>
              <CardContent>
                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <AfiliadoSelect
                    {...register('idAfiliado', {
                      validate: (value) => (value === null ? false : true),
                    })}
                    onSelectAfiliado={(afiliado) => {
                      setSelectedAfiliado(afiliado);
                      setValue('idAfiliado', afiliado?.afiliado?.idAfiliado);
                      setValue('documento', afiliado?.afiliado?.numeroDocumento);
                    }}
                  />
                  {errors?.idAfiliado && (
                    <p style={{ color: 'red' }}>Debe seleccionar el afiliado</p>
                  )}
                </FormControl>

                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <Controller
                    control={control}
                    name='fechaDesde'
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <TextField
                          type='date'
                          label='Desde'
                          variant='standard'
                          fullWidth
                          value={field?.value || ''}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger('fechaDesde');
                          }}
                          error={!!error}
                          helperText={error ? error.message : ''}
                        />
                      );
                    }}
                    rules={{
                      validate: (value) => {
                        const fechaHasta = getValues('fechaHasta');
                        if (!value || !fechaHasta) {
                          return true;
                        }
                        if (value <= fechaHasta) {
                          return true;
                        } else if (fechaHasta < value) {
                          return 'La fecha desde debe ser menor o igual a la fecha hasta.';
                        }
                      },
                    }}
                  />
                </FormControl>
                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <Controller
                    control={control}
                    name='fechaHasta'
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <TextField
                          type='date'
                          label='Hasta'
                          variant='standard'
                          fullWidth
                          value={field?.value || ''}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger('fechaHasta');
                          }}
                          error={!!error}
                          helperText={error ? error.message : ''}
                        />
                      );
                    }}
                    rules={{
                      validate: (value) => {
                        const fechaDesde = getValues('fechaDesde');
                        if (!value || !fechaDesde) {
                          return true;
                        }
                        if (value >= fechaDesde) {
                          return true;
                        } else if (fechaDesde > value) {
                          return 'La fecha hasta debe ser mayor o igual a la fecha hasta.';
                        }
                      },
                    }}
                  />
                </FormControl>
              </CardContent>

              <CardActions sx={{ px: 2 }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                      }}
                    >
                      <Button
                        variant='contained'
                        size='medium'
                        startIcon={<HighlightOff />}
                        color='neutral'
                        onClick={onHandlerLimpiar}
                      >
                        Limpiar
                      </Button>
                      <LoadingButton
                        type='submit'
                        variant='contained'
                        color='primary'
                        loading={isLoading}
                        startIcon={<SearchIcon />}
                      >
                        Buscar
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
              </CardActions>
            </form>
          )}
          {!showForm && prescripcionesElectronicas.length > 0 && (
            <Grid container p={2} pt={0}>
              <Grid item xs={12}>
                <PrescripcionElectronicaTableResult
                  key={keyComponentResultPrescripcionesElectronicas}
                  prescripcionesElectronicas={prescripcionesElectronicas}
                  onRefresh={refresh}
                  afiliadoSeleccionado={selectedAfiliado}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant='contained'
                    color='neutral'
                    startIcon={<SearchIcon />}
                    onClick={onHandlerBackToForm}
                  >
                    Buscar Otras
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Card>
      </Grid>

      <CustomizedSnackbars
        open={messageStatus?.status}
        autoHideDuration={8000}
        severity={messageStatus?.status || 'success'}
        message={messageStatus?.message}
        onDeleteMessage={onHandlerCleanMessage}
      />
    </Grid>
  );
};

export default withRouter(BuscarPrescripcionesElectronicas);
